import { useState, useEffect } from "react";

// Feld-Edit-Modal Komponente
export default function FieldEditModal({ field, onSave, onCancel, onOpenThematikManager, onOpenVeranstaltungManager, customThematikOptionen, customVeranstaltungOptionen, veranstaltungOptionen }) {
  const [value, setValue] = useState(field?.value || '');
  const [currentOptions, setCurrentOptions] = useState(field?.options || []);
  
  // States für den 2-Schritt DateTimePicker
  const [dateTimeStep, setDateTimeStep] = useState(1); // 1 = Datum, 2 = Uhrzeit
  const [tempDate, setTempDate] = useState("");
  const [tempTime, setTempTime] = useState("");
  
  // Initialisierung für datetime-custom
  useEffect(() => {
    if (field?.type === 'datetime-custom' && field?.value) {
      const dt = new Date(field.value);
      const yyyy = dt.getFullYear();
      const mm = String(dt.getMonth() + 1).padStart(2, '0');
      const dd = String(dt.getDate()).padStart(2, '0');
      const localDate = `${yyyy}-${mm}-${dd}`;
      const hours = String(dt.getHours()).padStart(2, '0');
      const minutes = String(dt.getMinutes()).padStart(2, '0');
      const localTime = `${hours}:${minutes}`;
      setTempDate(localDate);
      setTempTime(localTime);
    } else if (field?.type === 'datetime-custom') {
      setTempDate("");
      setTempTime("");
    }
  }, [field]);
  
  // Aktualisiere Options wenn sich field ändert oder custom-Listen ändern
  useEffect(() => {
    if (field?.name === 'thematik') {
      const DEFAULT_THEMATIK_OPTIONEN = [
        "Neurodermitis", "Diabetes", "Gewichtsmanagement", "Migräne",
        "Hautirritationen", "Bluthochdruck", "Arthritis", "Schlafstörungen",
        "Verdauungsprobleme", "Immunsystem", "Energiemangel", "Stress & Burnout",
        "Allergien", "Gelenkschmerzen", "Herzgesundheit", "Cholesterin",
        "Schilddrüse", "Wechseljahre", "Haarausfall", "Anti-Aging",
        "Sporternährung", "Detox & Entgiftung"
      ];
      const updatedOptions = [...DEFAULT_THEMATIK_OPTIONEN, ...(customThematikOptionen || [])];
      setCurrentOptions(updatedOptions);
    } else if (field?.name === 'online_live_veranstaltungen') {
      setCurrentOptions(veranstaltungOptionen || field?.options || []);
    } else if (field?.options) {
      setCurrentOptions(field.options);
    }
  }, [customThematikOptionen, veranstaltungOptionen, field?.name, field?.options]);
  
  if (!field) return null;
  
  const handleSave = () => {
    if (field.type === 'datetime-custom') {
      if (tempDate && tempTime) {
        const combined = `${tempDate}T${tempTime}`;
        onSave(field.name, combined);
      }
    } else {
      onSave(field.name, value);
    }
  };
  
  const handleDateTimeNext = () => {
    if (dateTimeStep === 1 && tempDate) {
      setDateTimeStep(2);
    }
  };
  
  const handleDateTimeBack = () => {
    setDateTimeStep(1);
  };
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
        <h3 className="text-lg font-bold text-slate-800 mb-4">
          {field.type === 'datetime-custom' 
            ? (dateTimeStep === 1 ? "Datum wählen" : "Uhrzeit wählen")
            : `${field.label} bearbeiten`}
        </h3>
        
        <div className="mb-6">
          {field.type === 'text' && (
            <input
              type="text"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              autoFocus
            />
          )}
          
          {field.type === 'textarea' && (
            <textarea
              value={value}
              onChange={(e) => setValue(e.target.value)}
              rows={4}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              autoFocus
            />
          )}
          
          {field.type === 'select' && (
            <select
              value={value}
              onChange={(e) => setValue(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              autoFocus
            >
              <option value="">-- Auswählen --</option>
              {field.options && field.options.map((opt, idx) => (
                <option key={opt} value={opt}>
                  {field.labels ? field.labels[idx] : opt}
                </option>
              ))}
            </select>
          )}
          
          {field.type === 'checkbox' && (
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={value}
                onChange={(e) => setValue(e.target.checked)}
                className="w-5 h-5 text-blue-600 border-slate-300 rounded focus:ring-2 focus:ring-blue-500"
              />
              <span className="text-sm text-slate-700">{field.label}</span>
            </label>
          )}
          
          {field.type === 'date' && (
            <input
              type="date"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              max={new Date().toISOString().split('T')[0]}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              autoFocus
            />
          )}
          
          {field.type === 'datetime' && (
            <input
              type="datetime-local"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              autoFocus
            />
          )}
          
          {field.type === 'datetime-custom' && dateTimeStep === 1 && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Datum</label>
              <input
                type="date"
                min={new Date().toISOString().split('T')[0]}
                value={tempDate}
                onChange={(e) => setTempDate(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg"
                autoFocus
              />
            </div>
          )}
          
          {field.type === 'datetime-custom' && dateTimeStep === 2 && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Uhrzeit</label>
              <input
                type="time"
                value={tempTime}
                onChange={(e) => setTempTime(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg"
                autoFocus
              />
            </div>
          )}
          
          {(field.type === 'multiselect' || field.type === 'multiselect-with-manager') && (
            <div>
              {field.type === 'multiselect-with-manager' && (
                <div className="flex justify-end mb-2">
                  <button
                    type="button"
                    onClick={() => {
                      if (field.name === 'thematik' && onOpenThematikManager) {
                        onOpenThematikManager();
                      } else if (field.name === 'online_live_veranstaltungen' && onOpenVeranstaltungManager) {
                        onOpenVeranstaltungManager();
                      }
                    }}
                    className="text-xs text-blue-600 hover:text-blue-700 underline font-semibold"
                  >
                    ⚙️ Liste bearbeiten
                  </button>
                </div>
              )}
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {(currentOptions.length > 0 ? currentOptions : field.options || []).map((opt, idx) => (
                  <label key={opt} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={Array.isArray(value) ? value.includes(opt) : false}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setValue(prev => Array.isArray(prev) ? [...prev, opt] : [opt]);
                        } else {
                          setValue(prev => Array.isArray(prev) ? prev.filter(v => v !== opt) : []);
                        }
                      }}
                      className="w-4 h-4 text-blue-600 border-slate-300 rounded"
                    />
                    <span className="text-sm text-slate-700">
                      {field.labels && field.labels[idx] ? field.labels[idx] : opt}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>
        
        <div className="flex gap-3 justify-end">
          {field.type === 'datetime-custom' && dateTimeStep === 1 ? (
            <>
              <button onClick={onCancel} className="px-4 py-2 text-slate-600 hover:text-slate-800 font-medium">
                Abbrechen
              </button>
              <button
                onClick={handleDateTimeNext}
                disabled={!tempDate}
                className="px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Weiter →
              </button>
            </>
          ) : field.type === 'datetime-custom' && dateTimeStep === 2 ? (
            <>
              <button onClick={handleDateTimeBack} className="px-4 py-2 text-slate-600 hover:text-slate-800 font-medium">
                ← Zurück
              </button>
              <button
                onClick={handleSave}
                disabled={!tempTime}
                className="px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Bestätigen
              </button>
            </>
          ) : (
            <>
              <button onClick={onCancel} className="px-4 py-2 text-slate-600 hover:text-slate-800 font-medium">
                Abbrechen
              </button>
              <button onClick={handleSave} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium">
                Speichern
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
